import type { ChangeEventHandler, FC } from "react";

import { Field, FieldError, FieldLabel } from "@recobra/ui/field";
import { Input } from "@recobra/ui/input";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  InputGroupText,
} from "@recobra/ui/input-group";

import { getErrorMessage, useFieldContext } from "./context";

export const NumberField: FC<{
  label: string;
  id?: string;
  suffix?: string;
  required?: boolean;
  min?: number;
  max?: number;
  step?: number | "any";
  inputMode?: "decimal" | "numeric";
}> = ({ label, id, suffix, required, min, max, step, inputMode = "decimal" }) => {
  const field = useFieldContext<number | undefined>();
  const errorMessage = getErrorMessage(field.state.meta.errors[0]);
  const updateValue: ChangeEventHandler<HTMLInputElement> = (event) => {
    const raw = event.target.value;
    field.handleChange(raw === "" ? undefined : Number(raw));
  };
  const controlProps = {
    id,
    type: "number" as const,
    inputMode,
    min,
    max,
    step,
    "aria-required": required,
    value: field.state.value ?? "",
    onChange: updateValue,
    onBlur: field.handleBlur,
    "aria-invalid": Boolean(errorMessage),
  };

  return (
    <Field data-invalid={errorMessage ? "true" : undefined} className="min-w-0">
      <FieldLabel htmlFor={id}>
        {label}
        {required && <span className="text-destructive">*</span>}
      </FieldLabel>
      {suffix ? (
        <InputGroup>
          <InputGroupInput {...controlProps} />
          <InputGroupAddon align="inline-end">
            <InputGroupText>{suffix}</InputGroupText>
          </InputGroupAddon>
        </InputGroup>
      ) : (
        <Input {...controlProps} />
      )}
      {errorMessage ? <FieldError>{errorMessage}</FieldError> : null}
    </Field>
  );
};
